import { Navigate } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import PayoutSetupRequests from "../components/PayoutSetupRequests";

const currency = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const GATEWAY_LABEL = {
  own: "Own gateway",
  platform: "Platform",
  mixed: "Mixed",
};

function GatewayBadge({ mode }) {
  const own = mode === "own";
  const mixed = mode === "mixed";
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${
        own
          ? "bg-sky-50 text-sky-700 border border-sky-200"
          : mixed
            ? "bg-amber-50 text-amber-700 border border-amber-200"
            : "bg-zinc-100 text-zinc-600 border border-zinc-200"
      }`}
      title={
        own
          ? "Paid into the organizer's own Razorpay account; commission is invoiced"
          : mixed
            ? "Some tickets were collected by Growithevent, some on the organizer's own account"
            : "Collected by Growithevent; host is settled manually"
      }
    >
      {GATEWAY_LABEL[mode] || "Platform"}
    </span>
  );
}

const formatDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return date.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

export default function SuperAdminDashboard() {
  const { superAdmin, superAdminLogout } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("ongoing");
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [section, setSection] = useState("events");

  useEffect(() => {
    api
      .get("/super-admin/overview")
      .then(({ data }) => setOverview(data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  if (!superAdmin) {
    return <Navigate to="/super-admin/login" replace />;
  }

  const ongoingTotals = overview?.totals || {};
  const pastTotals = overview?.past_totals || {};
  const totals = activeView === "past" ? pastTotals : ongoingTotals;
  const breakdown = overview?.fee_breakdown || {};
  const visibleEvents =
    activeView === "past"
      ? overview?.past_events || []
      : overview?.ongoing_events || [];

  return (
    <div className="min-h-screen bg-zinc-100 p-6">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center justify-between gap-4 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
              Access Control
            </p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-zinc-900">
              Super Admin Panel
            </h1>
          </div>

          <Button
            variant="outline"
            onClick={superAdminLogout}
            className="rounded-md"
          >
            Log out
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
            <p className="text-sm text-zinc-500">Signed in as</p>
            <p className="mt-2 text-xl font-semibold text-zinc-900">
              {superAdmin.login_id}
            </p>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
            <p className="text-sm text-zinc-500">Status</p>
            <p className="mt-2 text-xl font-semibold text-emerald-600">Active</p>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
            <p className="text-sm text-zinc-500">Mode</p>
            <p className="mt-2 text-xl font-semibold text-zinc-900">Manual settlement</p>
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
            <p className="text-sm text-zinc-500">Past events</p>
            <p className="mt-2 text-xl font-semibold text-zinc-900">
              {loading ? "..." : pastTotals.events || 0}
            </p>
          </div>
        </div>

        <div className="mt-6 flex rounded-lg border border-zinc-200 bg-white p-1 w-fit" role="tablist">
          {[
            { key: "events", label: "Events & settlements" },
            { key: "payout-setup", label: "Payout setup requests" },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={section === item.key}
              onClick={() => setSection(item.key)}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                section === item.key
                  ? "bg-zinc-900 text-white"
                  : "text-zinc-500 hover:text-zinc-900"
              }`}
              data-testid={`admin-section-${item.key}`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {section === "payout-setup" && (
          <div className="mt-4 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-zinc-900">Payout setup requests</h2>
            <p className="mt-2 mb-5 text-sm leading-6 text-zinc-600">
              Organizers' details for opening their Razorpay account from the Partner dashboard.
              Work through each one, then connect its keys here.
            </p>
            <PayoutSetupRequests />
          </div>
        )}

        {section === "events" && (
          <div className="mt-4 rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-zinc-900">
                  {activeView === "past" ? "Past events" : "Ongoing events"}
                </h2>
                <p className="mt-2 text-sm leading-6 text-zinc-600">
                  {activeView === "past"
                    ? "Historical paid tickets and the final settlement breakdown for completed events."
                    : "Paid tickets and settlement estimates for events scheduled today or later."}
                </p>
              </div>
              <div className="flex rounded-lg border border-zinc-200 bg-zinc-50 p-1">
                <button
                  type="button"
                  onClick={() => setActiveView("ongoing")}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    activeView === "ongoing"
                      ? "bg-zinc-900 text-white"
                      : "text-zinc-500 hover:text-zinc-900"
                  }`}
                >
                  Ongoing
                </button>
                <button
                  type="button"
                  onClick={() => setActiveView("past")}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    activeView === "past"
                      ? "bg-zinc-900 text-white"
                      : "text-zinc-500 hover:text-zinc-900"
                  }`}
                >
                  Past
                </button>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap gap-3 text-sm text-zinc-600">
              <span className="rounded-full bg-zinc-100 px-3 py-1.5">
                {totals.events || 0} events
              </span>
              <span className="rounded-full bg-zinc-100 px-3 py-1.5">
                {totals.paid_registrations || 0} paid registrations
              </span>
              <span className="rounded-full bg-zinc-100 px-3 py-1.5">
                {totals.tickets || 0} tickets paid
              </span>
            </div>

            <div className="mt-6 grid gap-3 md:grid-cols-3 lg:grid-cols-5">
              <div className="rounded-lg bg-zinc-100 p-4">
                <p className="text-xs text-zinc-500">Gross collections</p>
                <p className="mt-1 text-lg font-semibold">{currency(totals.gross)}</p>
                {totals.own_gateway_gross > 0 && (
                  <p className="mt-1 text-xs text-zinc-500">
                    incl. {currency(totals.own_gateway_gross)} paid directly to hosts
                  </p>
                )}
              </div>
              <div className="rounded-lg bg-zinc-100 p-4">
                <p className="text-xs text-zinc-500">Host net settlement · {breakdown.organizer_net_percent || 95}%</p>
                <p className="mt-1 text-lg font-semibold">{currency(totals.organizer_share)}</p>
                <p className="mt-1 text-xs text-zinc-500">platform-collected tickets only</p>
              </div>
              <div className="rounded-lg bg-zinc-100 p-4">
                <p className="text-xs text-zinc-500">Razorpay · {breakdown.razorpay_percent || 0}%</p>
                <p className="mt-1 text-lg font-semibold">{currency(totals.razorpay_fee)}</p>
              </div>
              <div className="rounded-lg bg-emerald-50 p-4">
                <p className="text-xs text-emerald-700">Your commission · {breakdown.platform_net_percent || 5}%</p>
                <p className="mt-1 text-lg font-semibold text-emerald-700">{currency(totals.platform_net)}</p>
                <p className="mt-1 text-xs text-emerald-700">already in hand</p>
              </div>
              <div className="rounded-lg bg-sky-50 p-4" data-testid="commission-receivable-tile">
                <p className="text-xs text-sky-700">Commission receivable · own-gateway events</p>
                <p className="mt-1 text-lg font-semibold text-sky-700">{currency(totals.commission_receivable)}</p>
                <p className="mt-1 text-xs text-sky-700">to invoice hosts paid directly</p>
              </div>
            </div>

            <div className="mt-6 overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="border-b border-zinc-200 text-xs uppercase tracking-wide text-zinc-500">
                  <tr>
                    <th className="pb-3 pr-4">Event</th>
                    <th className="pb-3 pr-4">Date</th>
                    <th className="pb-3 pr-4">Organizer</th>
                    <th className="pb-3 pr-4">Gateway</th>
                    <th className="pb-3 pr-4">Tickets</th>
                    <th className="pb-3 pr-4">Gross</th>
                    <th className="pb-3 pr-4">Host gross</th>
                    <th className="pb-3 pr-4">Razorpay {breakdown.razorpay_percent || 0}%</th>
                    <th className="pb-3">Your {breakdown.platform_net_percent || 5}%</th>
                    <th className="pb-3">Registrations</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan="10" className="py-8 text-center text-zinc-500">Loading events...</td></tr>
                  )}
                  {!loading && !visibleEvents.length && (
                    <tr><td colSpan="10" className="py-8 text-center text-zinc-500">No {activeView} events found.</td></tr>
                  )}
                  {visibleEvents.map((event) => (
                    <Fragment key={event.id}>
                      <tr className="border-b border-zinc-100">
                        <td className="py-4 pr-4 font-medium text-zinc-900">{event.name}</td>
                        <td className="py-4 pr-4 text-zinc-600">{formatDate(event.date)}</td>
                        <td className="py-4 pr-4 text-zinc-600">{event.organizer_email}</td>
                        <td className="py-4 pr-4"><GatewayBadge mode={event.gateway_mode} /></td>
                        <td className="py-4 pr-4 text-zinc-900">{event.tickets} / {event.capacity}</td>
                        <td className="py-4 pr-4 text-zinc-900">{currency(event.gross)}</td>
                        <td className="py-4 pr-4 text-zinc-900">
                          {event.gateway_mode === "own" ? (
                            <span className="text-zinc-500" title="Settled directly to the host's Razorpay account">Direct to host</span>
                          ) : (
                            currency(event.organizer_gross)
                          )}
                        </td>
                        <td className="py-4 pr-4 text-zinc-600">
                          {event.gateway_mode === "own" ? <span className="text-zinc-400">—</span> : currency(event.razorpay_fee)}
                        </td>
                        <td className="py-4 pr-4 font-semibold">
                          {event.gateway_mode === "own" ? (
                            <span className="text-sky-700" data-testid="event-commission-receivable">
                              {currency(event.commission_receivable)}
                              <span className="block text-xs font-normal">receivable</span>
                            </span>
                          ) : (
                            <span className="text-emerald-700">
                              {currency(event.platform_net)}
                              {event.commission_receivable > 0 && (
                                <span className="block text-xs font-normal text-sky-700">
                                  + {currency(event.commission_receivable)} receivable
                                </span>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="py-4">
                          <button
                            type="button"
                            onClick={() => setExpandedEventId(
                              expandedEventId === event.id ? null : event.id
                            )}
                            className="whitespace-nowrap rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:border-zinc-400 hover:text-zinc-950"
                          >
                            {expandedEventId === event.id
                              ? "Hide details"
                              : `View ${event.registration_count || 0}`}
                          </button>
                        </td>
                      </tr>
                      {expandedEventId === event.id && (
                        <tr>
                          <td colSpan="10" className="bg-zinc-50 px-4 py-5">
                            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                              <div>
                                <p className="font-semibold text-zinc-900">Registration details</p>
                                <p className="text-xs text-zinc-500">
                                  {event.paid_registrations || 0} paid registrations · {event.tickets || 0} tickets paid
                                </p>
                              </div>
                            </div>
                            {!event.registrations?.length ? (
                              <p className="rounded-lg border border-dashed border-zinc-300 bg-white p-5 text-center text-sm text-zinc-500">
                                No registrations for this event.
                              </p>
                            ) : (
                              <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white">
                                <table className="w-full min-w-[900px] text-left text-xs">
                                  <thead className="border-b border-zinc-200 bg-zinc-50 uppercase tracking-wide text-zinc-500">
                                    <tr>
                                      <th className="px-3 py-3">Attendee</th>
                                      <th className="px-3 py-3">Contact</th>
                                      <th className="px-3 py-3">Sponsor</th>
                                      <th className="px-3 py-3">Tickets</th>
                                      <th className="px-3 py-3">Payment</th>
                                      <th className="px-3 py-3">Attendance</th>
                                      <th className="px-3 py-3">Registered</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {event.registrations.map((registration) => (
                                      <tr key={registration.id} className="border-b border-zinc-100 last:border-0">
                                        <td className="px-3 py-3 font-medium text-zinc-900">{registration.name || "-"}</td>
                                        <td className="px-3 py-3 text-zinc-600">
                                          <div>{registration.email || "-"}</div>
                                          <div>{registration.phone || "-"}</div>
                                        </td>
                                        <td className="px-3 py-3 text-zinc-600">{registration.sponsor_name || "-"}</td>
                                        <td className="px-3 py-3 text-zinc-900">{registration.ticket_count}</td>
                                        <td className="px-3 py-3">
                                          <span className={registration.payment_status === "paid" ? "font-semibold text-emerald-700" : "text-amber-700"}>
                                            {registration.payment_status}
                                          </span>
                                          {registration.payment_mode && (
                                            <div className="text-zinc-500">
                                              {registration.payment_mode}
                                              {registration.gateway_mode === "own" && " · own gateway"}
                                            </div>
                                          )}
                                        </td>
                                        <td className="px-3 py-3 text-zinc-600">
                                          {registration.attendance_status} ({registration.checked_in_count})
                                        </td>
                                        <td className="px-3 py-3 text-zinc-600">{formatDate(registration.created_at)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}